// AIDEV-NOTE: Template PDF renderizado 100% client-side — funciona offline
// Usa apenas primitivos de @react-pdf/renderer (Document, Page, Text, View, Image)
// Nunca importar react-dom aqui

import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import type { Pedido, PedidoItem, Parcela } from '@azoupvendas/shared';
import type { EmpresaData, VendedorData } from '../services/pdf.service';

interface PedidoPDFProps {
  pedido: Pedido;
  empresa: EmpresaData;
  vendedor: VendedorData;
}

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
    fontSize: 10
  },
  // -- Header --
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    borderBottomWidth: 1,
    borderColor: '#ddd',
    paddingBottom: 10
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10
  },
  logo: {
    width: 80,
    height: 40
  },
  empresaInfo: {
    flexDirection: 'column'
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111111'
  },
  subtitle: {
    fontSize: 9,
    color: '#666'
  },
  pedidoInfo: {
    alignItems: 'flex-end'
  },
  // -- Sections --
  section: {
    marginBottom: 15
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#333',
    borderBottomWidth: 1,
    borderColor: '#eee',
    paddingBottom: 3
  },
  row: {
    flexDirection: 'row',
    marginBottom: 2
  },
  label: {
    fontSize: 9,
    color: '#666',
    width: '30%'
  },
  value: {
    fontSize: 9,
    color: '#333',
    width: '70%'
  },
  // -- Table --
  table: {
    flexDirection: 'column',
    width: '100%'
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#111111',
    padding: 5
  },
  tableHeaderCell: {
    color: 'white',
    fontSize: 9,
    fontWeight: 'bold'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: '#eee',
    padding: 4
  },
  tableRowAlt: {
    backgroundColor: '#f9f9f9'
  },
  cell: {
    fontSize: 9
  },
  // Column widths
  colProduto: { width: '30%' },
  colRef: { width: '12%' },
  colCor: { width: '10%' },
  colTam: { width: '8%' },
  colQtd: { width: '8%', textAlign: 'right' },
  colVlrUnit: { width: '12%', textAlign: 'right' },
  colDesc: { width: '8%', textAlign: 'right' },
  colTotal: { width: '12%', textAlign: 'right' },
  // -- Totals --
  totalSection: {
    marginTop: 10,
    alignItems: 'flex-end'
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '40%',
    marginBottom: 3
  },
  totalLabel: {
    fontSize: 10,
    color: '#333'
  },
  totalValue: {
    fontSize: 10,
    color: '#333',
    textAlign: 'right'
  },
  totalFinal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '40%',
    borderTopWidth: 2,
    borderColor: '#111111',
    paddingTop: 5
  },
  totalFinalText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#111111'
  },
  // -- Parcelas --
  parcelaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
    borderBottomWidth: 1,
    borderColor: '#f0f0f0'
  },
  parcelaText: {
    fontSize: 9,
    color: '#333'
  },
  // -- Observacao --
  observacao: {
    fontSize: 9,
    color: '#333',
    padding: 8,
    backgroundColor: '#f9f9f9',
    borderRadius: 3
  },
  // -- Signatures --
  signatureArea: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 50
  },
  signatureBox: {
    width: '45%',
    borderTopWidth: 1,
    borderColor: '#333',
    paddingTop: 5
  },
  signatureLabel: {
    fontSize: 9,
    textAlign: 'center',
    color: '#666'
  },
  // -- Footer --
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 8,
    color: '#999'
  }
});

// -- Helpers --

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR');
}

// -- Sections --

function HeaderSection({ pedido, empresa }: { pedido: Pedido; empresa: EmpresaData }) {
  const hasEmpresaInfo =
    empresa.logo_url ||
    (empresa.cnpj && empresa.cnpj.length > 0) ||
    empresa.fone ||
    empresa.endereco;

  return (
    <View style={styles.header}>
      <View style={styles.headerLeft}>
        {empresa.logo_url ? <Image style={styles.logo} src={empresa.logo_url} /> : null}
        {hasEmpresaInfo && (
          <View style={styles.empresaInfo}>
            {empresa.cnpj ? <Text style={styles.subtitle}>CNPJ: {empresa.cnpj}</Text> : null}
            {empresa.fone ? <Text style={styles.subtitle}>Fone: {empresa.fone}</Text> : null}
            {empresa.endereco ? <Text style={styles.subtitle}>{empresa.endereco}</Text> : null}
          </View>
        )}
      </View>
      <View style={styles.pedidoInfo}>
        <Text style={styles.title}>
          PEDIDO {pedido.numero ? `N. ${pedido.numero}` : '(Rascunho)'}
        </Text>
        <Text style={styles.subtitle}>
          Data: {pedido.created_at ? formatDate(pedido.created_at) : '--'}
        </Text>
        <Text style={styles.subtitle}>Status: {pedido.status?.toUpperCase() ?? ''}</Text>
      </View>
    </View>
  );
}

function ClienteSection({ pedido }: { pedido: Pedido }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Dados do Cliente</Text>
      <View style={styles.row}>
        <Text style={styles.label}>Razao Social:</Text>
        <Text style={styles.value}>{pedido.cliente_nome}</Text>
      </View>
      <View style={styles.row}>
        <Text style={styles.label}>CPF/CNPJ:</Text>
        <Text style={styles.value}>{pedido.cliente_cpf_cnpj}</Text>
      </View>
    </View>
  );
}

function ItensTable({ itens }: { itens: PedidoItem[] }) {
  const usaGrade = itens.some((i) => (i.codcor ?? 0) > 0 || (i.codtamanho ?? 0) > 0);
  // AIDEV-NOTE: quando sem grade, redistribui os 18% (colCor+colTam) para colProduto
  const colProdutoStyle = usaGrade ? styles.colProduto : { width: '48%' };
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Itens do Pedido</Text>
      <View style={styles.table}>
        {/* Header */}
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, colProdutoStyle]}>Produto</Text>
          <Text style={[styles.tableHeaderCell, styles.colRef]}>Ref</Text>
          {usaGrade && <Text style={[styles.tableHeaderCell, styles.colCor]}>Cor</Text>}
          {usaGrade && <Text style={[styles.tableHeaderCell, styles.colTam]}>Tam</Text>}
          <Text style={[styles.tableHeaderCell, styles.colQtd]}>Qtd</Text>
          <Text style={[styles.tableHeaderCell, styles.colVlrUnit]}>Vlr Unit</Text>
          <Text style={[styles.tableHeaderCell, styles.colDesc]}>Desc%</Text>
          <Text style={[styles.tableHeaderCell, styles.colTotal]}>Total</Text>
        </View>
        {/* Rows */}
        {itens.map((item, idx) => (
          <View
            key={item.id ?? `item-${idx}`}
            style={[styles.tableRow, idx % 2 === 1 ? styles.tableRowAlt : {}]}
          >
            <Text style={[styles.cell, colProdutoStyle]}>{item.descricao}</Text>
            <Text style={[styles.cell, styles.colRef]}>{item.referencia}</Text>
            {usaGrade && <Text style={[styles.cell, styles.colCor]}>{item.cor ?? '--'}</Text>}
            {usaGrade && <Text style={[styles.cell, styles.colTam]}>{item.tamanho ?? '--'}</Text>}
            <Text style={[styles.cell, styles.colQtd]}>{item.quantidade}</Text>
            <Text style={[styles.cell, styles.colVlrUnit]}>
              {formatCurrency(item.valor_unitario)}
            </Text>
            <Text style={[styles.cell, styles.colDesc]}>
              {item.desconto_percentual > 0 ? `${item.desconto_percentual}%` : '--'}
            </Text>
            <Text style={[styles.cell, styles.colTotal]}>{formatCurrency(item.total_item)}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

// AIDEV-NOTE: Resumo de totais do PDF. Subtotal é o valor BRUTO (sem descontos) e a linha
// de desconto soma os descontos por item + desconto geral — antes, descontos por item
// ficavam embutidos no subtotal e invisíveis nos totais. Number() protege contra NUMERIC
// do pg serializado como string no JSON da API. Exportada para teste unitário.
export function calcularResumoTotais(pedido: Pedido): {
  subtotalBruto: number;
  descontoTotal: number;
  percentualGeral: number;
} {
  const itens = pedido.itens ?? [];
  const subtotalBruto = itens.reduce(
    (sum, item) => sum + Number(item.quantidade) * Number(item.valor_unitario),
    0
  );
  const totalProdutos = Number(pedido.total_produtos ?? 0);
  const descontoItens = Math.max(0, subtotalBruto - totalProdutos);
  const descontoGeral = Number(pedido.total_desconto ?? 0);
  const descontoTotal = Math.round((descontoItens + descontoGeral) * 100) / 100;
  // AIDEV-NOTE: % efetivo DERIVADO dos valores — desconto da condicao de pagamento entra em
  // total_desconto mas nao em desconto_geral_percentual (coluna guarda so o manual). Antes,
  // desconto vindo so da condicao aparecia sem % no PDF (bug reportado 2026-08-04).
  const percentualGeral =
    subtotalBruto > 0 && descontoTotal > 0
      ? Math.round((descontoTotal / subtotalBruto) * 10000) / 100
      : Number(pedido.desconto_geral_percentual ?? 0);
  return {
    subtotalBruto,
    descontoTotal,
    percentualGeral
  };
}

function TotaisSection({ pedido }: { pedido: Pedido }) {
  const { subtotalBruto, descontoTotal, percentualGeral } = calcularResumoTotais(pedido);
  const temDescontoItens = subtotalBruto - Number(pedido.total_produtos ?? 0) > 0.001;
  return (
    <View style={styles.totalSection}>
      <View style={styles.totalRow}>
        <Text style={styles.totalLabel}>Subtotal Produtos:</Text>
        <Text style={styles.totalValue}>{formatCurrency(subtotalBruto)}</Text>
      </View>
      {descontoTotal > 0 && (
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>
            {/* AIDEV-NOTE: % geral só no rótulo quando é o único desconto — com desconto
                por item junto, o % não representaria o valor exibido */}
            Desconto{!temDescontoItens && percentualGeral > 0 ? ` (${percentualGeral}%)` : ''}:
          </Text>
          <Text style={styles.totalValue}>- {formatCurrency(descontoTotal)}</Text>
        </View>
      )}
      <View style={styles.totalFinal}>
        <Text style={styles.totalFinalText}>TOTAL FINAL:</Text>
        <Text style={styles.totalFinalText}>{formatCurrency(pedido.total_final)}</Text>
      </View>
    </View>
  );
}

function ParcelasSection({ parcelas }: { parcelas: Parcela[] }) {
  if (parcelas.length === 0) return null;

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Condicao de Pagamento</Text>
      {parcelas.map((p) => (
        <View key={`parcela-${p.numero}`} style={styles.parcelaRow}>
          <Text style={styles.parcelaText}>Parcela {p.numero}</Text>
          <Text style={styles.parcelaText}>Vencimento: {formatDate(p.vencimento)}</Text>
          <Text style={styles.parcelaText}>{formatCurrency(p.valor)}</Text>
        </View>
      ))}
    </View>
  );
}

function ObservacaoSection({ observacao }: { observacao: string }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Observacao</Text>
      <Text style={styles.observacao}>{observacao}</Text>
    </View>
  );
}

function AssinaturasSection({ vendedorNome }: { vendedorNome: string }) {
  return (
    <View style={styles.signatureArea}>
      <View style={styles.signatureBox}>
        <Text style={styles.signatureLabel}>Cliente</Text>
      </View>
      <View style={styles.signatureBox}>
        <Text style={styles.signatureLabel}>Vendedor: {vendedorNome}</Text>
      </View>
    </View>
  );
}

// -- Main Component --

export function PedidoPDF({ pedido, empresa, vendedor }: PedidoPDFProps) {
  const now = new Date().toLocaleString('pt-BR');

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* 1. Cabecalho */}
        <HeaderSection pedido={pedido} empresa={empresa} />

        {/* 2. Dados do Cliente */}
        <ClienteSection pedido={pedido} />

        {/* 3. Tabela de Itens */}
        <ItensTable itens={pedido.itens ?? []} />

        {/* 4. Totais */}
        <TotaisSection pedido={pedido} />

        {/* 5. Condicao de Pagamento */}
        <ParcelasSection parcelas={pedido.parcelas ?? []} />

        {/* 6. Observacao (apenas se existir) */}
        {pedido.observacao ? <ObservacaoSection observacao={pedido.observacao} /> : null}

        {/* 7. Assinaturas */}
        <AssinaturasSection vendedorNome={vendedor.nome} />

        {/* 8. Footer */}
        <Text style={styles.footer}>Documento gerado por AzoupVendas em {now}</Text>
      </Page>
    </Document>
  );
}
